import React, { createContext, useState, useCallback, useEffect } from 'react';
import api from '../services/api';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState({
    token: null,
    user: null,
    isAuthenticated: false,
    loading: true,
  });

  useEffect(() => {
    // Check if we have stored auth data (from previous login in same session)
    const storedToken = sessionStorage.getItem('token');
    const storedUser = sessionStorage.getItem('user');

    if (storedToken && storedUser) {
      setAuth({
        token: storedToken,
        user: JSON.parse(storedUser),
        isAuthenticated: true,
        loading: false,
      });
    } else {
      setAuth((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, userId, tenantId, role } = response.data;

      const user = { id: userId, tenantId, email, role };
      setAuth({
        token,
        user,
        isAuthenticated: true,
        loading: false,
      });

      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(user));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return { success: true, role };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed',
      };
    }
  }, []);

  const register = useCallback(async (tenantName, email, password) => {
    try {
      const response = await api.post('/auth/register', {
        tenantName,
        email,
        password,
      });
      const { token, userId, tenantId } = response.data;

      const user = { id: userId, tenantId, email };
      setAuth({
        token,
        user,
        isAuthenticated: true,
        loading: false,
      });

      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(user));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed',
      };
    }
  }, []);

  const logout = useCallback(() => {
    setAuth({
      token: null,
      user: null,
      isAuthenticated: false,
      loading: false,
    });
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
  }, []);

  return (
    <AuthContext.Provider value={{ ...auth, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
